import divingCatchTuningData from '../../data/diving-catch-tuning.json';

type Point = {
  x: number;
  y: number;
};

type DivingCatchTuning = {
  minimumCatchPointSkill: number;
  minimumAttemptCatchPointSkill: number;
  minimumInterceptionCatchPointSkill: number;
  minimumCatchQuality: number;
  minimumInterceptionCatchQuality: number;
  minimumInterceptionReachYards: number;
  maximumInterceptionReachYards: number;
  minimumInterceptionLongitudinalReachYards: number;
  maximumPlannedYacYards: number;
  minimumReachYards: number;
  maximumReachYards: number;
  minimumForwardReachYards: number;
  minimumAttemptReachYards: number;
  maximumAttemptReachYards: number;
  minimumAttemptForwardReachYards: number;
  baseTransferFrames: number;
  maximumTransferFrames: number;
  launchEndProgress: number;
  contactProgress: number;
  landingProgress: number;
  maximumLaunchTravelYards: number;
  minimumLaunchTravelYards: number;
  landingSkidYards: number;
  maximumVerticalLiftYards: number;
  verticalLiftApexProgress: number;
  groundRecoveryHoldFrames: number;
  groundRecoveryRiseFrames: number;
  heroMoment: {
    minimumAirYards: number;
    completionChance: number;
    attemptChance: number;
    interceptionChance: number;
    leadInFrames: number;
    followThroughFrames: number;
    slowMotionRate: number;
    zoomScale: number;
    soundVolume: number;
  };
};

const DIVING_CATCH_TUNING = divingCatchTuningData as DivingCatchTuning;

export type ArcadeDivingCatchState = {
  startCarrierX: number;
  startCarrierY: number;
  catchPointX: number;
  catchPointY: number;
  directionX: number;
  directionY: number;
  launchTravelYards: number;
  skidYards: number;
  reachYards: number;
  totalFrames: number;
};

export type ArcadeDivingCatchPose = {
  x: number;
  y: number;
  assignmentKind:
    | 'catch-dive-launch'
    | 'catch-dive-extension'
    | 'catch-dive-contact'
    | 'catch-dive-landing'
    | 'catch-dive-down';
  ballSecureProgress: number;
  progress: number;
};

export type ArcadeDivingCatchRecoveryState = {
  totalFrames: number;
  framesRemaining: number;
  groundHoldFrames: number;
  riseFrames: number;
};

function createDivingCatchMotionState(options: {
  carrier: Point;
  catchAnchor: Point;
  ball: Point;
  catchPointSkill: number;
  minimumCatchPointSkill?: number;
  minimumReachYards?: number;
  maximumReachYards?: number;
  minimumForwardReachYards?: number;
  allowReverseReach?: boolean;
}): ArcadeDivingCatchState | null {
  const catchPointSkill = clamp(options.catchPointSkill, 0, 1);
  const minimumCatchPointSkill = options.minimumCatchPointSkill
    ?? DIVING_CATCH_TUNING.minimumCatchPointSkill;
  if (catchPointSkill < minimumCatchPointSkill) {
    return null;
  }

  const reachX = options.ball.x - options.catchAnchor.x;
  const reachY = options.ball.y - options.catchAnchor.y;
  const reachYards = Math.hypot(reachX, reachY);
  const minimumReachYards = options.minimumReachYards ?? DIVING_CATCH_TUNING.minimumReachYards;
  const maximumReachYards = options.maximumReachYards ?? DIVING_CATCH_TUNING.maximumReachYards;
  const minimumForwardReachYards = options.minimumForwardReachYards
    ?? DIVING_CATCH_TUNING.minimumForwardReachYards;
  const longitudinalReach = options.allowReverseReach ? Math.abs(reachX) : reachX;
  const forwardOverthrow = longitudinalReach >= minimumForwardReachYards;
  if (
    !forwardOverthrow
    || reachYards < minimumReachYards
    || reachYards > maximumReachYards
  ) {
    return null;
  }

  const directionX = reachX / Math.max(0.001, reachYards);
  const directionY = reachY / Math.max(0.001, reachYards);
  const ratingTravelBonus = (catchPointSkill - DIVING_CATCH_TUNING.minimumCatchPointSkill) * 0.42;
  const launchTravelYards = clamp(
    reachYards * 0.86 + 0.26 + ratingTravelBonus,
    DIVING_CATCH_TUNING.minimumLaunchTravelYards,
    DIVING_CATCH_TUNING.maximumLaunchTravelYards,
  );
  const frameRange = Math.max(
    0,
    DIVING_CATCH_TUNING.maximumTransferFrames - DIVING_CATCH_TUNING.baseTransferFrames,
  );
  const reachFrameShare = clamp(
    (reachYards - minimumReachYards)
      / Math.max(0.001, maximumReachYards - minimumReachYards),
    0,
    1,
  );
  const totalFrames = clamp(
    Math.round(DIVING_CATCH_TUNING.baseTransferFrames + frameRange * reachFrameShare),
    DIVING_CATCH_TUNING.baseTransferFrames,
    DIVING_CATCH_TUNING.maximumTransferFrames,
  );

  return {
    startCarrierX: options.carrier.x,
    startCarrierY: options.carrier.y,
    catchPointX: options.ball.x,
    catchPointY: options.ball.y,
    directionX,
    directionY,
    launchTravelYards,
    skidYards: DIVING_CATCH_TUNING.landingSkidYards,
    reachYards,
    totalFrames,
  };
}

function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value));
}

function smoothStep(value: number): number {
  const clamped = clamp(value, 0, 1);
  return clamped * clamped * (3 - 2 * clamped);
}

export function createArcadeDivingCatchState(options: {
  carrier: Point;
  catchAnchor: Point;
  ball: Point;
  result: 'complete' | 'interception';
  catchPointSkill: number;
  catchQuality?: number;
  plannedYacYards?: number;
  allowInterception?: boolean;
}): ArcadeDivingCatchState | null {
  if (options.result === 'interception' && !options.allowInterception) {
    return null;
  }

  const catchPointSkill = clamp(options.catchPointSkill, 0, 1);
  const catchQuality = clamp(options.catchQuality ?? 0.72, 0, 1);
  const minimumCatchPointSkill = options.result === 'interception'
    ? DIVING_CATCH_TUNING.minimumInterceptionCatchPointSkill
    : DIVING_CATCH_TUNING.minimumCatchPointSkill;
  const minimumCatchQuality = options.result === 'interception'
    ? DIVING_CATCH_TUNING.minimumInterceptionCatchQuality
    : DIVING_CATCH_TUNING.minimumCatchQuality;
  if (
    catchPointSkill < minimumCatchPointSkill
    || catchQuality < minimumCatchQuality
    || Math.max(0, options.plannedYacYards ?? 0) > DIVING_CATCH_TUNING.maximumPlannedYacYards
  ) {
    return null;
  }

  return createDivingCatchMotionState({
    carrier: options.carrier,
    catchAnchor: options.catchAnchor,
    ball: options.ball,
    catchPointSkill,
    minimumCatchPointSkill,
    minimumReachYards: options.result === 'interception'
      ? DIVING_CATCH_TUNING.minimumInterceptionReachYards
      : undefined,
    maximumReachYards: options.result === 'interception'
      ? DIVING_CATCH_TUNING.maximumInterceptionReachYards
      : undefined,
    minimumForwardReachYards: options.result === 'interception'
      ? DIVING_CATCH_TUNING.minimumInterceptionLongitudinalReachYards
      : undefined,
    allowReverseReach: options.result === 'interception',
  });
}

/** A ratings-gated layout attempt that never changes an incomplete outcome. */
export function createArcadeDivingCatchAttemptState(options: {
  carrier: Point;
  catchAnchor: Point;
  ball: Point;
  catchPointSkill: number;
}): ArcadeDivingCatchState | null {
  return createDivingCatchMotionState({
    ...options,
    minimumCatchPointSkill: DIVING_CATCH_TUNING.minimumAttemptCatchPointSkill,
    minimumReachYards: DIVING_CATCH_TUNING.minimumAttemptReachYards,
    maximumReachYards: DIVING_CATCH_TUNING.maximumAttemptReachYards,
    minimumForwardReachYards: DIVING_CATCH_TUNING.minimumAttemptForwardReachYards,
  });
}

export function arcadeDivingCatchPoseAtFrame(
  state: ArcadeDivingCatchState,
  framesRemaining: number,
): ArcadeDivingCatchPose {
  const elapsedFrames = state.totalFrames - framesRemaining + 1;
  const progress = clamp(elapsedFrames / Math.max(1, state.totalFrames), 0, 1);
  const contactProgress = DIVING_CATCH_TUNING.contactProgress;
  const landingProgress = DIVING_CATCH_TUNING.landingProgress;
  const launchProgress = clamp(progress / Math.max(0.001, contactProgress), 0, 1);
  const landingPhase = smoothStep(
    (progress - contactProgress) / Math.max(0.001, 1 - contactProgress),
  );
  const travelYards = state.launchTravelYards * launchProgress + state.skidYards * landingPhase;
  const ballSecureProgress = progress < contactProgress
    ? 0
    : smoothStep((progress - contactProgress) / Math.max(0.001, landingProgress - contactProgress));

  const assignmentKind = progress < DIVING_CATCH_TUNING.launchEndProgress
    ? 'catch-dive-launch'
    : progress < contactProgress
      ? 'catch-dive-extension'
      : progress < landingProgress
        ? 'catch-dive-contact'
        : progress < 1
          ? 'catch-dive-landing'
          : 'catch-dive-down';

  return {
    x: state.startCarrierX + state.directionX * travelYards,
    y: state.startCarrierY + state.directionY * travelYards,
    assignmentKind,
    ballSecureProgress,
    progress,
  };
}

export function arcadeDivingCatchVisualLiftYardsAtProgress(progress: number): number {
  const clampedProgress = clamp(progress, 0, 1);
  const apexProgress = DIVING_CATCH_TUNING.verticalLiftApexProgress;
  const arcProgress = clampedProgress <= apexProgress
    ? (clampedProgress / Math.max(0.001, apexProgress)) * 0.5
    : 0.5 + (
        (clampedProgress - apexProgress)
        / Math.max(0.001, 1 - apexProgress)
      ) * 0.5;
  return Math.sin(arcProgress * Math.PI) * DIVING_CATCH_TUNING.maximumVerticalLiftYards;
}

export function arcadeDivingCatchVisualLiftYards(assignmentKind: string | null | undefined): number {
  if (assignmentKind === 'catch-dive-launch') return arcadeDivingCatchVisualLiftYardsAtProgress(0.14);
  if (assignmentKind === 'catch-dive-extension') return arcadeDivingCatchVisualLiftYardsAtProgress(0.42);
  if (assignmentKind === 'catch-dive-contact') return arcadeDivingCatchVisualLiftYardsAtProgress(0.64);
  if (assignmentKind === 'catch-dive-landing') return arcadeDivingCatchVisualLiftYardsAtProgress(0.88);
  return 0;
}

export function createArcadeDivingCatchRecoveryState(): ArcadeDivingCatchRecoveryState {
  const groundHoldFrames = Math.max(1, Math.round(DIVING_CATCH_TUNING.groundRecoveryHoldFrames));
  const riseFrames = Math.max(1, Math.round(DIVING_CATCH_TUNING.groundRecoveryRiseFrames));
  return {
    totalFrames: groundHoldFrames + riseFrames,
    framesRemaining: groundHoldFrames + riseFrames,
    groundHoldFrames,
    riseFrames,
  };
}

export function groundArcadeDivingCatch(carrier: {
  vx: number;
  vy: number;
  state: string;
  assignmentKind: string | null;
  engagementState: string | null;
  tackleProgress: number;
  beingTackledBy: string[];
}): void {
  carrier.vx = 0;
  carrier.vy = 0;
  carrier.state = 'grounded';
  carrier.assignmentKind = 'catch-dive-down';
  carrier.engagementState = 'free';
  carrier.tackleProgress = 0;
  carrier.beingTackledBy = [];
}

export function advanceArcadeDivingCatchRecovery(
  carrier: {
    vx: number;
    vy: number;
    state: string;
    assignmentKind: string | null;
    engagementState: string | null;
    tackleProgress: number;
    beingTackledBy: string[];
  },
  recovery: ArcadeDivingCatchRecoveryState,
): boolean {
  carrier.vx = 0;
  carrier.vy = 0;
  carrier.engagementState = 'free';
  carrier.tackleProgress = 0;
  carrier.beingTackledBy = [];

  if (recovery.framesRemaining > recovery.riseFrames) {
    carrier.state = 'grounded';
    carrier.assignmentKind = 'catch-dive-down';
  } else {
    carrier.state = 'recovering';
    carrier.assignmentKind = 'catch-dive-rise';
  }

  recovery.framesRemaining -= 1;
  if (recovery.framesRemaining > 0) {
    return false;
  }

  carrier.state = 'running';
  carrier.assignmentKind = 'run-after-catch';
  return true;
}

export function finishArcadeDivingCatch(carrier: {
  vx: number;
  vy: number;
  state: string;
  assignmentKind: string | null;
  engagementState: string | null;
  tackleProgress: number;
  beingTackledBy: string[];
}): void {
  carrier.vx = 0;
  carrier.vy = 0;
  carrier.state = 'down';
  carrier.assignmentKind = 'catch-dive-down';
  carrier.engagementState = 'tackled';
  carrier.tackleProgress = 1;
  carrier.beingTackledBy = [];
}

export function divingCatchTuningForTests(): Readonly<DivingCatchTuning> {
  return DIVING_CATCH_TUNING;
}
